import { createContext, useState, useEffect, useContext } from "react";

const VersionCheckContext = createContext({
  hasNewVersion: false,
  currentVersion: null,
  dismissUpdate: () => {},
  reloadApp: () => {},
});

export const VersionCheckProvider = ({ children }) => {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        // Buscar versão do servidor
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_LINK}/api/version`,
        );
        const data = await response.json();
        const serverVersion = data.version;

        // Buscar versão armazenada localmente
        const storedVersion = localStorage.getItem("appVersion");
        const updateDismissed = localStorage.getItem(
          `updateDismissed_${serverVersion}`,
        );

        // Se não tem versão armazenada, guardar a atual
        if (!storedVersion) {
          localStorage.setItem("appVersion", serverVersion);
          setCurrentVersion(serverVersion);
          return;
        }

        // Se a versão mudou E o utilizador não dispensou
        if (serverVersion !== storedVersion && !updateDismissed) {
          setHasNewVersion(true);
          setCurrentVersion(serverVersion);
        } else {
          setCurrentVersion(serverVersion);
        }
      } catch (error) {
        console.error("Erro ao verificar versão:", error);
      }
    };

    checkVersion();
  }, []);

  const dismissUpdate = () => {
    if (currentVersion) {
      localStorage.setItem(`updateDismissed_${currentVersion}`, "true");
      setHasNewVersion(false);
    }
  };

  const reloadApp = async () => {
    if (currentVersion) {
      localStorage.setItem("appVersion", currentVersion);
      localStorage.removeItem(`updateDismissed_${currentVersion}`);

      // Limpar caches do Service Worker / Cache API
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Desregistar Service Workers se existirem
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }

      // Reload sem cache (abordagem compatível com browsers modernos)
      window.location.href = window.location.href;
    }
  };

  return (
    <VersionCheckContext.Provider
      value={{
        hasNewVersion,
        currentVersion,
        dismissUpdate,
        reloadApp,
      }}
    >
      {children}
    </VersionCheckContext.Provider>
  );
};

export const useVersionCheck = () => {
  const context = useContext(VersionCheckContext);
  if (!context) {
    throw new Error("useVersionCheck must be used within VersionCheckProvider");
  }
  return context;
};
